
import { mapGetters } from "vuex";

export default {
    name: "lobby_map",
    props: ["set", "isZoneSelected"],
    data(){
        return {
            activeKey: null,
            isMapReady: false,
        
            cta: [
                {
                    title: "Fiesta Jukebox",
                    desc: "Press play and see what you'll score!",
                    slug: "jukebox",
                },
                {
                    title: "Claim Your Bonus Points",
                    desc: "Got a code? Turn it into bonus points!",
                    slug: "bonus_code",
                },
            ]
        }
    },
    computed: {
        ...mapGetters({
            stampItems: "stamp/stampItems",
        }),
        svgW(){
            return this.lobbyMap.imageW;
        },
        svgH(){
            return this.lobbyMap.imageH;
        },
        isMobile(){
            if(!this.winW) return false;
            return this.winW < 768 || this.winH < 500;
        },
        mapImage(){
            const item = this.isMobile? (this.lobbyMap.imageMobile || this.lobbyMap.image) : this.lobbyMap.image;
            return this.formattedAssetUrl(item);
        },
        iconRadius(){
            const size = this.isMobile? this.lobbyMap.iconSizeMobile : this.lobbyMap.iconSize;
            return (size / 100) * this.svgW / 2;
        },
        activeArea(){
            if(!this.activeKey) return null;
            return _.find(this.lobbyMap.areas, { key: this.activeKey }) || null;
        },
        visibleAreas(){
            return this.lobbyMap.areas.filter(area => area.key != this.activeKey);
        },
        viewBox(){
            const { focus } = this.lobbyMap;
            const item = this.isMobile
                ? (this.activeKey? focus.mobileActive : focus.mobile)
                : (this.activeKey? focus.desktopActive : focus.desktop);

            return `${ this.toX(item.x) } ${ this.toY(item.y) } ${ this.toX(item.width) } ${ this.toY(item.height) }`;
        },
        mapBoxStyle(){
            const { inset } = this.lobbyMap;
            return this.isMobile
                ? (this.activeKey? inset.mobileActive : inset.mobile)
                : (this.activeKey? inset.desktopActive : inset.desktop);
        },
        skyStyle(){
            const { skyFrom, skyVia, skyTo } = this.lobbyMap;
            return { background: `linear-gradient(to bottom, ${ skyFrom } 0%, ${ skyVia } 55%, ${ skyTo } 100%)` };
        },
        skyImageStyle(){
            return { background: `url(${ this.mapImage }) center bottom / cover no-repeat` };
        },
        panelBoxStyle(){
            return this.isMobile? this.lobbyMap.panel.mobile : this.lobbyMap.panel.desktop;
        },
        overlayMaskImage(){
            if(!this.activeArea) return null;

            const x = -this.svgW * 2;
            const y = -this.svgH * 2;
            const outer = `M${ x },${ y } H${ x + this.svgW * 5 } V${ y + this.svgH * 5 } H${ x } Z`;
            const hole = `M${ this.activeArea.area.map(point => `${ this.toX(point[0]) },${ this.toY(point[1]) }`).join(" L") } Z`;
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${ this.viewBox }" preserveAspectRatio="${ this.lobbyMap.align } ${ this.lobbyMap.fit }"><path fill="#ffffff" fill-rule="evenodd" d="${ outer } ${ hole }"/></svg>`;

            return `url("data:image/svg+xml,${ encodeURIComponent(svg) }")`;
        },
        overlayStyle(){
            const mask = this.overlayMaskImage;

            return {
                ...this.mapBoxStyle,
                backgroundColor: this.toRgba(this.lobbyMap.overlayColor, this.lobbyMap.overlayOpacity),
                ...mask && {
                    maskImage: mask,
                    WebkitMaskImage: mask,
                    maskSize: "100% 100%",
                    WebkitMaskSize: "100% 100%",
                    maskRepeat: "no-repeat",
                    WebkitMaskRepeat: "no-repeat",
                }
            };
        },
        gotoBoxStyle(){
            return this.isMobile? this.lobbyMap.goto.mobile : this.lobbyMap.goto.desktop;
        },
        setLabel(){
            return this.set? String(this.set).replace("ocbc360_", "") : "";
        },
        foundItems(){
            if(!this.activeArea || !this.activeArea.slug || !this.setLabel || !this.stampItems) return [];

            const room = this.roomItems[this.activeArea.slug];
            const foundItemsData = _.filter(this.stampItems, item => {
                return item.title.includes("Scavenger Hunt") && item.title.includes(`Set ${ this.setLabel }`) && item.room == room;
            });

            return _.compact(foundItemsData.map(item => {
                const matched = item.title.match(/Scavenger Hunt (1|2|3|4|5)/);
                return matched? parseInt(matched[1]) : null;
            }));
        },
    },
    mounted(){
        this.$emit("onLoadMap");
        this.onPreloadMapImage();
    },
    beforeDestroy(){
        this.$emit("update:isZoneSelected", false);
    },
    methods: {
        onPreloadMapImage(){
            const sources = [this.mapImage, ...this.lobbyMap.areas.map(area => this.iconSrc(area))];

            Promise.all(sources.map(source => new Promise(resolve => {
                const image = new Image();
                image.onload = () => resolve();
                image.onerror = () => resolve();
                image.src = source;
                if(typeof image.decode === "function") image.decode().then(resolve).catch(resolve);
            }))).then(() => {
                this.isMapReady = true;
            });
        },
        toRgba(hex, opacity){
            const matched = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
            if(!matched) return hex;

            const [r, g, b] = matched.slice(1).map(part => parseInt(part, 16));
            return `rgba(${ r }, ${ g }, ${ b }, ${ opacity })`;
        },
        toX(item){
            return (item / 100) * this.svgW;
        },
        toY(item){
            return (item / 100) * this.svgH;
        },
        toPoints(items){
            if(!items) return "";
            return items.map(([x, y]) => `${ this.toX(x) },${ this.toY(y) }`).join(" ");
        },
        iconSrc(item){
            return this.formattedAssetUrl(`${ this.lobbyMap.iconPath }/${ item.icon }.png`);
        },
        descImageSrc(item){
            if(!item.descImage) return `${ this.lobbyMap.iconPath }/${ item.icon }.png`;
            return `${ this.lobbyMap.descPath }/${ item.descImage }`;
        },
        areaTitle(item){
            return item.title || this.roomItems[item.slug] || "";
        },
        onClickArea(item){
            if(this.activeKey == item.key) return;

            this.activeKey = item.key;
            // this.onHandleTriggerAnalytics(`open@map@${ item.key }`);
        },
        onClickCloseArea(){
            if(!this.activeKey) return;
            // this.onHandleTriggerAnalytics(`close@map@${ this.activeKey }`);
            this.activeKey = null;
        },
        onClickGoToZone(){
            if(!this.activeArea || !this.activeArea.slug) return;
            this.$emit("onClickNavItem", this.activeArea.slug);
        },
        onClickOpenPopup(slug){
            if(!this.activeArea) return;
            this.$emit("onClickNavItem", slug);
        },
    },
    watch: {
        "activeKey"(value){
            this.$emit("update:isZoneSelected", !!value);
        }
    }
}
